import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { NiraBar } from "../ds/NiraBar"
import { Rail } from "../answer/Rail"
import { usePageTitle } from "../answer/hooks"
import "../answer/answer.css"
import "../answer/answer-v4.css"
import "../ds/ds.css"
import "../landing/landing.css"
import "../matter/WorkspaceCard.css"
import "../matter/MatterFiles.css"
import "../matter/matter.css"
import "./case.css"
import "./workspaceAbout.css"
import { NlSection } from "../landing/NlSection"
import { ABOUT_CLOSE, ABOUT_FAQ, ABOUT_HELPS, ABOUT_HERO, ABOUT_TOUR } from "./aboutWorkspace"
import { Tour } from "./about/Tour"
import { useReveal } from "./about/useReveal"

function Actions() {
  return (
    <div className="wa-acts">
      <Link className="nr nr-btn" data-variant="primary" to={ABOUT_HERO.primary.to}>{ABOUT_HERO.primary.label}</Link>
      <Link className="nr nr-btn" to={ABOUT_HERO.secondary.to}>{ABOUT_HERO.secondary.label}</Link>
    </div>
  )
}

/** A hairline-ruled accordion: each question is a real button, so Tab, Enter and Space work. */
function Faq() {
  const [open, setOpen] = useState<number | null>(0)
  return (
    <div className="wa-faq">
      {ABOUT_FAQ.items.map((f, i) => {
        const on = open === i
        return (
          <div key={f.q} className="wa-qa" data-open={on ? "true" : "false"}>
            <h3>
              <button type="button" id={`wa-q${i}`} aria-expanded={on} aria-controls={`wa-a${i}`} onClick={() => setOpen(on ? null : i)}>
                <span>{f.q}</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m4 6 4 4 4-4" /></svg>
              </button>
            </h3>
            <div id={`wa-a${i}`} role="region" aria-labelledby={`wa-q${i}`} hidden={!on}><p>{f.a}</p></div>
          </div>
        )
      })}
    </div>
  )
}

/** Where the reader came from (router state `from`), when it is a path inside the app. */
function originOf(state: unknown): string | null {
  const from = (state as { from?: unknown } | null)?.from
  return typeof from === "string" && from.startsWith("/") && !from.startsWith("//") ? from : null
}

/** The explainer for workspaces: what one is, a four-step walkthrough, why it helps. Copy lives in aboutWorkspace.ts. */
export default function WorkspaceAbout() {
  const [histOpen, setHistOpen] = useState(false)
  const [helpsRef, helpsSeen] = useReveal<HTMLOListElement>()
  usePageTitle("What is a workspace?")
  // Opened from an answer or a workspace page: a way straight back to it, so nothing is lost.
  const from = originOf(useLocation().state)
  const toAnswer = from?.startsWith("/nira/") ?? false
  const backLabel = toAnswer ? "Your answer" : "Back"
  return (
    <div className="nl-landing nh wa">
      <div className="na2 na4 nh-drawer">
        <Rail threadId="" histOpen={histOpen} onHist={setHistOpen} refreshKey={0} drawerOnly />
        <div className={`scrim${histOpen ? " on" : ""}`} aria-hidden="true" onClick={() => setHistOpen(false)} />
      </div>
      <NiraBar onMenu={() => setHistOpen((v) => !v)} menuOpen={histOpen} menuControls="nira-hlist" crumbs={[{ label: "Home", to: "/ask" }, from ? { label: backLabel, to: from } : { label: "Workspaces", to: "/workspaces" }, { label: "What is a workspace?" }]} />
      <main className="nl-main">
        <section className="nl-first" aria-label="Workspaces">
          <div className="nl-stack">
            {from ? (
              <Link className="wa-back" to={from}>
                <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12.5 4.5 7 10l5.5 5.5" /></svg>
                {toAnswer ? "Back to your answer" : "Back"}
              </Link>
            ) : null}
            <p className="nl-overline">{ABOUT_HERO.overline}</p>
            <h1>{ABOUT_HERO.title}</h1>
            <p className="nl-sub">{ABOUT_HERO.sub}</p>
            <Actions />
          </div>
        </section>

        <div className="nl-wrap nl-more">
          <NlSection id="wa-how" eyebrow={ABOUT_TOUR.eyebrow} title={ABOUT_TOUR.title}>
            <Tour />
          </NlSection>

          <NlSection id="wa-helps" eyebrow={ABOUT_HELPS.eyebrow} title={ABOUT_HELPS.title}>
            <ol ref={helpsRef} className="nl-steps wa-helps" data-in={helpsSeen ? "true" : "false"}>
              {ABOUT_HELPS.points.map((p, i) => (
                <li key={p.title} style={{ "--i": i } as React.CSSProperties}><span className="wa-n">{String(i + 1).padStart(2, "0")}</span><h3>{p.title}</h3><p>{p.text}</p></li>
              ))}
            </ol>
          </NlSection>

          <NlSection id="wa-faq" eyebrow={ABOUT_FAQ.eyebrow} title={ABOUT_FAQ.title}>
            <Faq />
          </NlSection>

          <NlSection id="wa-close" eyebrow="Get started" title={ABOUT_CLOSE.title}>
            <p className="nl-sub">{ABOUT_CLOSE.sub}</p>
            <Actions />
          </NlSection>
        </div>
      </main>
    </div>
  )
}
